import React, { useCallback, useEffect, useState, useRef, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { ChevronRightIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
interface SubDropdownProps {
  label: string;
  icon?: ReactNode;
  isActive: boolean;
  onToggle: (e: React.MouseEvent) => void;
  children: ReactNode;
}
const MENU_WIDTH = 224; // w-56
const SubDropdown = ({
  label,
  icon,
  isActive,
  onToggle,
  children
}: SubDropdownProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dk = useDarkClasses();
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const calcPosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceOnRight = window.innerWidth - rect.right;
    const spaceOnLeft = rect.left;
    let left: number;
    if (spaceOnRight >= MENU_WIDTH + 8) {
      left = rect.right + 4;
    } else if (spaceOnLeft >= MENU_WIDTH + 8) {
      left = rect.left - MENU_WIDTH - 4;
    } else {
      left = rect.right + 4; // fallback right
    }
    const maxHeight = window.innerHeight - rect.top - 16;
    setMenuStyle({
      position: 'fixed',
      top: rect.top,
      left,
      width: MENU_WIDTH,
      maxHeight: Math.min(maxHeight, window.innerHeight * 0.6),
      zIndex: 9999
    });
  }, []);
  useLayoutEffect(() => {
    if (isActive) calcPosition();
  }, [isActive, calcPosition]);
  // Close on outside click
  useEffect(() => {
    if (!isActive) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Element;
      if (target.closest('[data-subdropdown-toggle]')) return;
      if (target.closest('[data-dropdown]')) return;
      const syntheticEvent = {
        stopPropagation: () => {},
        preventDefault: () => {}
      } as React.MouseEvent;
      onToggle(syntheticEvent);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isActive, onToggle]);
  const dropdownBg = dk.isDark ? 'bg-gray-800 ring-1 ring-gray-700' : 'bg-white ring-1 ring-black ring-opacity-5';
  const buttonBaseClasses = 'w-full text-left px-4 py-2 text-sm flex items-center justify-between';
  const buttonActiveLight = 'bg-purple-50 text-purple-700';
  const buttonActiveDark = 'bg-purple-900/50 text-purple-300';
  const buttonInactiveLight = 'text-gray-700 hover:bg-gray-50 hover:text-gray-900';
  const buttonInactiveDark = 'text-gray-200 hover:bg-gray-700 hover:text-gray-100';
  return <div className="relative" data-subdropdown data-id="element-2792" data-mp="3oy3c19etr87o">
      <button ref={buttonRef} className={`${buttonBaseClasses} ${isActive ? dk.isDark ? buttonActiveDark : buttonActiveLight : dk.isDark ? buttonInactiveDark : buttonInactiveLight}`} onClick={onToggle} data-subdropdown-toggle data-id="element-2793" data-mp="3oywazo7jktf9">
        <span className="flex items-center" data-id="element-2794" data-mp="2rh4mjysayavr">
          {icon && <span className="mr-2" data-id="element-2795" data-mp="1705602oinmjl">{icon}</span>}
          {label}
        </span>
        <ChevronRightIcon className={`ml-2 h-4 w-4 transition-transform ${isActive ? 'rotate-90' : ''}`} data-id="element-2796" data-mp="16y25uptngd15" />
      </button>
      {isActive && ReactDOM.createPortal(<div data-dropdown style={menuStyle} className={`rounded-md shadow-lg overflow-y-auto ${dropdownBg}`} role="menu" aria-orientation="vertical" data-id="element-2797" data-mp="x3sbj2l5d4ns">
            <div className="py-1" role="none" data-id="element-2798" data-mp="1upppcmol0lte">
              {children}
            </div>
          </div>, document.body)}
    </div>;
};
export default SubDropdown;